import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "../../lib/utils"

const ResponsiveTabsContext = React.createContext(() => {})

const countTabTriggers = (children) => React.Children.toArray(children).reduce((count, child) => {
  if (!React.isValidElement(child)) return count
  if (child.type === React.Fragment) return count + countTabTriggers(child.props.children)
  return count + 1
}, 0)

const Tabs = ({ children, orientation, ...props }) => {
  const [compactPortrait, setCompactPortrait] = React.useState(() => (
    typeof window !== "undefined" && window.matchMedia("(max-width: 1023px) and (orientation: portrait)").matches
  ))
  const [tabCount, setTabCount] = React.useState(0)

  React.useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px) and (orientation: portrait)")
    const update = () => setCompactPortrait(media.matches)
    update()
    media.addEventListener("change", update)
    return () => media.removeEventListener("change", update)
  }, [])

  const resolvedOrientation = orientation || (compactPortrait && tabCount > 2 ? "vertical" : "horizontal")
  return (
    <ResponsiveTabsContext.Provider value={setTabCount}>
      <TabsPrimitive.Root {...props} orientation={resolvedOrientation}>
        {children}
      </TabsPrimitive.Root>
    </ResponsiveTabsContext.Provider>
  )
}

const TabsList = React.forwardRef(({ className, children, onKeyDown, ...props }, ref) => {
  const setTabCount = React.useContext(ResponsiveTabsContext)
  const tabCount = countTabTriggers(children)

  React.useEffect(() => {
    setTabCount(tabCount)
  }, [setTabCount, tabCount])

  const handleKeyDown = (event) => {
    onKeyDown?.(event)
    if (event.currentTarget.getAttribute("aria-orientation") !== "vertical") return
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    const triggers = [...event.currentTarget.querySelectorAll('[role="tab"]:not([disabled])')]
    const currentIndex = triggers.indexOf(document.activeElement)
    if (currentIndex < 0) return

    event.preventDefault()
    const direction = event.key === "ArrowDown" ? 1 : -1
    triggers[(currentIndex + direction + triggers.length) % triggers.length]?.focus()
  }

  return (
    <TabsPrimitive.List
      ref={ref}
      data-rf-tab-count={tabCount}
      className={cn(
        "rf-responsive-tabs inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
        className
      )}
      {...props}
      onKeyDownCapture={handleKeyDown}
    >
      {children}
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    data-testid={`responsive-tab-${props.value}`}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
